/**
 * Канонический URL сайта — единый источник.
 * Использовать вместо локального `const SITE_URL = "https://..."`.
 *
 * Где применять: metadataBase, canonical, JSON-LD @id/url, OG-images,
 * RSS feed, sitemap, CSRF ALLOWED_ORIGINS.
 *
 * taliora-west: домен задаётся через env `NEXT_PUBLIC_SITE_DOMAIN`
 * (локалка → http://localhost:3000; прод → западный домен бренда).
 * Fallback `https://zaruvia.com` — только пока env не выставлен; убрать
 * после выбора бренда/домена. Меняется в ОДНОМ месте — здесь + env.
 */
const RAW_SITE_URL = process.env.NEXT_PUBLIC_SITE_DOMAIN || "https://zaruvia.com";

/** Канонический URL без хвостового слэша (для конкатенации путей). */
export const SITE_URL = RAW_SITE_URL.replace(/\/+$/, "");

/** Хост без протокола (для robots host, sitemap host и т.п.). */
export const SITE_HOST = SITE_URL.replace(/^https?:\/\//, "");

/**
 * Название бренда — единый источник.
 * taliora-west: бренд ещё не выбран (кандидаты Sinastra / Lunara), задаётся
 * через env `NEXT_PUBLIC_SITE_BRAND`. Дефолт «Zaruvia» — placeholder.
 */
export const SITE_BRAND = process.env.NEXT_PUBLIC_SITE_BRAND || "Zaruvia";

/**
 * Канонический часовой пояс сайта (IANA), единый источник.
 * Все «сегодня»-расчёты (@/lib/numerology/today) и display-форматирование дат
 * (`toLocaleDateString(..., { timeZone: SITE_TIMEZONE })`) идут через него.
 *
 * taliora-west: задаётся через env `NEXT_PUBLIC_SITE_TIMEZONE`
 * (NEXT_PUBLIC — нужен и на клиенте). Дефолт `America/Mexico_City` (LatAm-first).
 *
 * ⚠️ В отличие от Москвы, западные зоны используют DST — поэтому расчёт
 * идёт через `Intl` (см. today.ts), а НЕ через фиксированный offset.
 *
 * Единый site-TZ (не персональный per-user) — осознанное решение: публичные
 * страницы кэшируются ISR для всех, персональная TZ юзера — отдельный этап.
 */
export const SITE_TIMEZONE = process.env.NEXT_PUBLIC_SITE_TIMEZONE || "America/Mexico_City";

/**
 * Etiqueta legible de la zona horaria del sitio, para textos de cara al usuario
 * («… a las 00:00 (hora de Ciudad de México)»).
 *
 * ⚠️ NUNCA escribas la ciudad a mano en el copy. Precedente 2026-07: varias
 * plantillas de email decían «(hora de Madrid)» mientras SITE_TIMEZONE era
 * `America/Mexico_City` → la hora mostrada mentía hasta 7 h. Al derivarla de
 * SITE_TIMEZONE, cambiar la zona actualiza el copy solo.
 *
 * Override opcional: `NEXT_PUBLIC_SITE_TIMEZONE_LABEL`.
 */
const TZ_CITIES: Record<string, string> = {
  "America/Mexico_City": "Ciudad de México",
  "Europe/Madrid": "Madrid",
  "America/Bogota": "Bogotá",
  "America/Argentina/Buenos_Aires": "Buenos Aires",
  "America/Santiago": "Santiago",
  "America/Lima": "Lima",
};

/** Solo la ciudad — para etiquetas compactas junto a una hora («14:03 · Ciudad de México»). */
export const SITE_TIMEZONE_CITY =
  process.env.NEXT_PUBLIC_SITE_TIMEZONE_LABEL ||
  TZ_CITIES[SITE_TIMEZONE] ||
  (SITE_TIMEZONE.split("/").pop() ?? SITE_TIMEZONE).replace(/_/g, " ");

/** Frase completa — para prosa («… a las 00:00 (hora de Ciudad de México)»). */
export const SITE_TIMEZONE_LABEL = `hora de ${SITE_TIMEZONE_CITY}`;
